import { styled } from '@linaria/react';
import { ChartFilterControls } from '../comparison-view/components/chart-filter-controls';
import { WorkPeriodsFilter } from '../filter/work-periods-filter';
import { type MeasureMetadata, WorkPeriodsTab } from '../work-periods.type';

type WorkPeriodsUnifiedFiltersProps = {
  // Common props
  isComparisonView?: boolean;

  // Comparison view specific props
  activeTab?: WorkPeriodsTab;
  customMeasures?: MeasureMetadata[];

  // Chart filter controls props for measure comparison
  selectedMeasures?: string[];
  onMeasuresChange?: (measures: string[]) => void;

  // Chart filter controls props for measures over time
  selectedMeasure?: string;
  onMeasureChange?: (measure: string | null) => void;
  showTrendLine?: boolean;
  onShowTrendLineChange?: (show: boolean) => void;
  showDots?: boolean;
  onShowDotsChange?: (show: boolean) => void;
};

/**
 * Unified filters panel component for work periods
 * Handles filters for both assessment and comparison views based on current tab
 */
export function WorkPeriodsUnifiedFilters({
  isComparisonView = false,
  activeTab,
  customMeasures = [],
  selectedMeasures = [],
  onMeasuresChange,
  selectedMeasure = '',
  onMeasureChange,
  showTrendLine = false,
  onShowTrendLineChange,
  showDots = true,
  onShowDotsChange,
}: WorkPeriodsUnifiedFiltersProps) {
  const renderChartFilters = () => {
    if (!isComparisonView || !activeTab || customMeasures.length === 0) {
      return null;
    }

    // Show chart-specific filters only for chart tabs
    if (activeTab === WorkPeriodsTab.COMPARISON_MEASURE_COMPARISON && onMeasuresChange) {
      return (
        <ChartFilterControls
          customMeasures={customMeasures}
          selectedMeasure={selectedMeasures}
          onMeasureChange={onMeasuresChange}
          isMultiSelect={true}
        />
      );
    }

    if (
      activeTab === WorkPeriodsTab.COMPARISON_MEASURES_OVER_TIME &&
      onMeasureChange &&
      onShowTrendLineChange &&
      onShowDotsChange
    ) {
      return (
        <ChartFilterControls
          customMeasures={customMeasures}
          selectedMeasure={selectedMeasure}
          onMeasureChange={onMeasureChange}
          showTrendLine={showTrendLine}
          onShowTrendLineChange={onShowTrendLineChange}
          showDots={showDots}
          onShowDotsChange={onShowDotsChange}
        />
      );
    }

    return null;
  };

  const chartFilters = renderChartFilters();

  return (
    <FiltersContainer>
      {chartFilters && <ChartFiltersSection>{chartFilters}</ChartFiltersSection>}

      <WorkPeriodsSection>
        <WorkPeriodsFilter isComparisonView={isComparisonView} />
      </WorkPeriodsSection>
    </FiltersContainer>
  );
}

const FiltersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0px;
`;

const ChartFiltersSection = styled.div`
  padding: 12px 12px 0px 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const WorkPeriodsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0px;
`;
